
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { Project, User, UserRole, ProjectStage, ScoreEntry, DevPerformance, Priority, Comment, HistoryItem } from './types';
import { INITIAL_CHECKLISTS } from './constants';
import { differenceInDays, isSameMonth } from 'date-fns';
import { db } from './database';
import { MOCK_USERS } from './mockData';

interface AppContextType {
  currentUser: User | null;
  users: User[];
  projects: Project[];
  scores: ScoreEntry[];
  clients: string[];
  isSyncing: boolean;
  isLoading: boolean;
  isAuthenticated: boolean;
  setCurrentUser: (user: User | null) => void;
  login: (username: string, pass: string) => Promise<boolean>;
  updateUserPassword: (userId: string, newPass: string) => Promise<void>;
  logout: () => void;
  addProject: (p: Partial<Project>) => Promise<void>;
  updateProject: (id: string, updates: Partial<Project>) => Promise<void>;
  addUser: (u: User) => Promise<void>;
  deleteUser: (userId: string) => Promise<void>;
  addComment: (projectId: string, text: string) => Promise<void>;
  advanceStage: (id: string, nextStage: ProjectStage, userId: string) => Promise<void>;
  recordQAFeedback: (id: string, passed: boolean, userId: string) => Promise<void>;
  regressToDev: (id: string, userId: string) => Promise<void>;
  regressToDesign: (id: string, userId: string, note: string) => Promise<void>;
  getDevRankings: (date?: Date) => DevPerformance[];
  exportSystemData: () => void;
  importSystemData: (json: string) => Promise<void>;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [scores, setScores] = useState<ScoreEntry[]>([]);

  const loadAllData = async (silent = false) => {
    if (!silent) setIsSyncing(true);
    try {
      const response = await db.query('load_all');
      
      if (response.users) {
        setUsers(response.users);
        if (response.users.length === 0) {
          // If fresh DB, seed initial admin
          for (const u of MOCK_USERS) {
            await db.query('add_user', { user: u });
          }
          const retry = await db.query('load_all');
          setUsers(retry.users);
        }
      }
      
      if (response.scores) setScores(response.scores);
      if (response.projects) setProjects(response.projects);
      
    } catch (error) {
      console.error('MySQL Data Load Failure:', error);
    } finally {
      if (!silent) setIsSyncing(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      setIsLoading(true);
      const savedUser = localStorage.getItem('nice_pms_auth_user');
      if (savedUser) {
        const parsed = JSON.parse(savedUser);
        setCurrentUser(parsed);
        setIsAuthenticated(true);
      }
      await loadAllData();
      setIsLoading(false);
    };
    init();

    // Polling as a fallback for Realtime (since raw MySQL doesn't have native web sockets)
    const interval = setInterval(() => loadAllData(true), 30000); 
    return () => clearInterval(interval);
  }, []);

  const login = async (username: string, pass: string): Promise<boolean> => {
    setIsLoading(true);
    try {
      const response = await db.query('login', { username, password: pass });
      if (response.user) {
        setCurrentUser(response.user);
        setIsAuthenticated(true);
        localStorage.setItem('nice_pms_auth_user', JSON.stringify(response.user));
        setIsLoading(false);
        return true;
      }
    } catch (e) {
      console.error('Login error:', e);
    }
    setIsLoading(false);
    return false;
  };

  const logout = () => {
    setIsAuthenticated(false);
    setCurrentUser(null);
    localStorage.removeItem('nice_pms_auth_user');
  };

  const updateUserPassword = async (userId: string, newPass: string) => {
    setIsSyncing(true);
    await db.query('update_password', { userId, password: newPass });
    await loadAllData();
  };

  const addUser = async (u: User) => {
    setIsSyncing(true);
    await db.query('add_user', { user: u });
    await loadAllData();
  };

  const deleteUser = async (userId: string) => {
    setIsSyncing(true);
    await db.query('delete_user', { userId });
    await loadAllData();
  };

  const addProject = async (p: Partial<Project>) => {
    setIsSyncing(true);
    const newProject: any = {
      id: `p-${Date.now()}`,
      name: p.name || 'Untitled Project',
      clientName: p.clientName || 'Unknown',
      scope: p.scope || '',
      priority: p.priority || Priority.MEDIUM,
      stage: ProjectStage.UPCOMING,
      overallDeadline: p.overallDeadline || new Date().toISOString(),
      currentDeadline: p.currentDeadline || p.overallDeadline,
      designChecklist: INITIAL_CHECKLISTS.DESIGN.map(i => ({ ...i, completed: false })),
      devChecklist: INITIAL_CHECKLISTS.DEVELOPMENT.map(i => ({ ...i, completed: false })),
      qaChecklist: INITIAL_CHECKLISTS.QA.map(i => ({ ...i, completed: false })),
      finalChecklist: INITIAL_CHECKLISTS.FINAL.map(i => ({ ...i, completed: false })),
      isDelayed: false,
      qaFailCount: 0,
      history: [{ stage: ProjectStage.UPCOMING, timestamp: new Date().toISOString(), userId: currentUser?.id || 'system', action: 'Project Created' }],
      createdAt: new Date().toISOString(),
      assignedDesignerId: p.assignedDesignerId || null,
      assignedDevManagerId: p.assignedDevManagerId || null,
      assignedQAId: null,
      designDeadline: p.designDeadline || null,
      devDeadline: p.devDeadline || null
    };
    
    await db.query('add_project', { project: newProject });
    await loadAllData();
  };

  const updateProject = async (id: string, updates: Partial<Project>) => {
    setIsSyncing(true);
    // Optimistic local update
    setProjects(prev => prev.map(p => p.id === id ? { ...p, ...updates } : p));
    
    await db.query('update_project', { id, updates });
    await loadAllData(true);
  };

  const addComment = async (projectId: string, text: string) => {
    if (!currentUser) return;
    setIsSyncing(true);
    const newComment = { 
      id: `c-${Date.now()}`, 
      projectId,
      userId: currentUser.id, 
      text, 
      timestamp: new Date().toISOString() 
    };
    await db.query('add_comment', { comment: newComment });
    await loadAllData();
  };

  const addScore = async (userId: string, projectId: string, points: number, reason: string) => {
    const newScore = { projectId, date: new Date().toISOString(), points, reason };
    await db.query('add_score', { score: newScore });
  };

  const recordQAFeedback = async (id: string, passed: boolean, userId: string) => {
    const project = projects.find(p => p.id === id);
    if (!project || !project.assignedDevManagerId) return;

    if (passed) {
      await advanceStage(id, ProjectStage.SEND_TO_CLIENT, userId);
    } else {
      const deduction = project.qaFailCount === 0 ? -10 : -25;
      await addScore(project.assignedDevManagerId, project.id, deduction, `QA Rejection #${project.qaFailCount + 1} Penalty`);

      const updatedHistory = [...project.history, { 
        stage: ProjectStage.QA, 
        timestamp: new Date().toISOString(), 
        userId, 
        action: `QA Failed (#${project.qaFailCount + 1}) - Returned to Dev`, 
        rejectionSnapshot: [...project.qaChecklist] 
      }];

      await updateProject(id, {
        stage: ProjectStage.DEVELOPMENT,
        qaFailCount: project.qaFailCount + 1,
        qaChecklist: project.qaChecklist.map(i => ({ ...i, completed: false })),
        history: updatedHistory
      });
    }
  };

  const advanceStage = async (id: string, nextStage: ProjectStage, userId: string) => {
    const p = projects.find(proj => proj.id === id);
    if (!p) return;

    const actionTextMap: Record<string, string> = {
      [ProjectStage.DESIGN]: 'Submitted to Design',
      [ProjectStage.DEVELOPMENT]: 'Submitted to Development',
      [ProjectStage.QA]: 'Submitted to QA',
      [ProjectStage.SEND_TO_CLIENT]: 'Submitted to Admin Review',
      [ProjectStage.SENT_TO_CLIENT]: 'Submitted to Client',
      [ProjectStage.COMPLETED]: 'Project Completed'
    };

    const actionText = actionTextMap[nextStage] || `Transition to ${nextStage}`;
    const historyEntry: HistoryItem = { stage: p.stage, timestamp: new Date().toISOString(), userId, action: actionText };
    const updates: Partial<Project> = { stage: nextStage, history: [...p.history, historyEntry] };
    
    if (nextStage === ProjectStage.COMPLETED && p.assignedDevManagerId) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const overallDeadlineDate = new Date(p.overallDeadline);
      overallDeadlineDate.setHours(0, 0, 0, 0);
      
      let projectScore = 100;
      const delayDays = differenceInDays(today, overallDeadlineDate);
      if (delayDays > 0) {
        projectScore -= (Math.min(delayDays, 5) * 10);
      }

      await addScore(p.assignedDevManagerId, p.id, projectScore, `Project Completion Reward (Days Delay: ${Math.max(0, delayDays)})`);
      updates.completedAt = new Date().toISOString();
    }
    
    await updateProject(id, updates);
  };

  const regressToDev = async (id: string, userId: string) => {
    const p = projects.find(proj => proj.id === id);
    if (!p) return;
    await updateProject(id, { 
      stage: ProjectStage.DEVELOPMENT, 
      history: [...p.history, { stage: p.stage, timestamp: new Date().toISOString(), userId, action: 'Client Revision - Back to Dev' }] 
    });
  };

  const regressToDesign = async (id: string, userId: string, note: string) => {
    const p = projects.find(proj => proj.id === id);
    if (!p) return;
    await updateProject(id, { 
      stage: ProjectStage.DESIGN, 
      designChecklist: p.designChecklist.map(i => ({ ...i, completed: false })), 
      history: [...p.history, { stage: p.stage, timestamp: new Date().toISOString(), userId, action: 'Returned to Design Phase', note }] 
    });
  };

  const getDevRankings = useCallback((targetDate: Date = new Date()) => {
    const devManagers = users.filter(u => u.role === UserRole.DEV_MANAGER);
    return devManagers.map(dev => {
      const devProjects = projects.filter(p => p.assignedDevManagerId === dev.id);
      const devScores = scores.filter(s => {
        const project = projects.find(p => p.id === s.projectId);
        return project?.assignedDevManagerId === dev.id && isSameMonth(new Date(s.date), targetDate);
      });
      
      const monthlyPoints = devScores.reduce((acc, s) => acc + s.points, 0);
      const completedThisMonth = devProjects.filter(p => p.stage === ProjectStage.COMPLETED && p.completedAt && isSameMonth(new Date(p.completedAt), targetDate));
      const onTime = devProjects.filter(p => p.stage === ProjectStage.COMPLETED && p.completedAt && new Date(p.completedAt) <= new Date(p.overallDeadline)).length;
      const qaFails = devProjects.reduce((acc, p) => acc + p.qaFailCount, 0);
      const qaPasses = devProjects.filter(p => p.stage !== ProjectStage.DESIGN && p.stage !== ProjectStage.DEVELOPMENT && p.stage !== ProjectStage.UPCOMING).length;

      return {
        userId: dev.id,
        userName: dev.name,
        totalPoints: scores.filter(s => projects.find(p => p.id === s.projectId)?.assignedDevManagerId === dev.id).reduce((acc, s) => acc + s.points, 0),
        monthlyPoints,
        completedProjects: completedThisMonth.length,
        qaFailureRate: (qaPasses + qaFails) > 0 ? (qaFails / (qaPasses + qaFails)) * 100 : 0,
        onTimeDeliveryRate: devProjects.filter(p => p.stage === ProjectStage.COMPLETED).length > 0 ? (onTime / devProjects.filter(p => p.stage === ProjectStage.COMPLETED).length) * 100 : 100,
      };
    }).sort((a, b) => b.monthlyPoints - a.monthlyPoints);
  }, [users, projects, scores]);

  const clients = useMemo(() => Array.from(new Set(projects.map(p => p.clientName))).sort(), [projects]);

  const exportSystemData = () => {
    const data = { exportDate: new Date().toISOString(), projects, users, scores };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `nice-digital-backup-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
  };

  const importSystemData = async (json: string) => {
    try {
      const data = JSON.parse(json);
      await db.query('bulk_import', { data });
      await loadAllData();
      alert("Import successful.");
    } catch (e) {
      console.error('Import Error:', e);
      alert("Invalid backup file.");
    }
  };

  return (
    <AppContext.Provider value={{
      currentUser, users, projects, scores, clients, isSyncing, isLoading, isAuthenticated,
      setCurrentUser, login, updateUserPassword, logout, addProject, updateProject, addUser, deleteUser, addComment, advanceStage, recordQAFeedback, regressToDev, regressToDesign, getDevRankings, exportSystemData, importSystemData
    }}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) throw new Error('useApp must be used within AppProvider');
  return context;
};
